/**
 * ¿Está el negocio listo para vender?
 *
 * One place that answers it, from the data rather than from a flag somebody has to remember to
 * set. A shop is ready when it has a name, something to sell and a price on the thing it is
 * selling — and until then every screen that assumes otherwise is a dead end somebody walks into.
 *
 * An open till was the fourth step, until the owner took it out (2026-10-08: «the shop doesn't
 * still need setting up unless there are prices missing»). A closed till is every evening and
 * every morning, not a shop left unfinished, and it only ever counted on the phone that opened
 * it: on one that never does, the owner's own, «Falta terminar de configurar el negocio · Paso 4
 * de 4» never went away. The home screen's big button says «Empezar el día» when it is closed.
 *
 * The check that matters most is `precios`. A product with no price used to go into the cart at
 * C$0.00, and the ledger booked the full cost as a loss; the POS refuses that now, but refusing
 * at the counter with a customer waiting is the wrong moment to find out. This is how it gets
 * found out beforehand.
 *
 * Nothing here writes. It reads the projections the same way any screen does, so it is always
 * describing the shop as it actually is, including on a phone that has been offline all day.
 */

import { useLiveQuery } from 'dexie-react-hooks';
import { DEFAULT_SETTINGS } from '@abastece/shared';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { listaPredeterminada, precioDe, unidadesDe } from './catalogo.js';

/** The three things that stand between a new account and its first sale, in the order to do them. */
export const PASOS = ['nombre', 'productos', 'precios'] as const;
export type ClavePaso = (typeof PASOS)[number];

/**
 * Things a shop needs eventually, but not to ring up its first sale. A truck was the other, until
 * the owner took «Reparto» out (2026-10-04).
 */
export const LUEGO = ['proveedor'] as const;
export type ClaveLuego = (typeof LUEGO)[number];

export interface Paso {
  clave: ClavePaso;
  hecho: boolean;
  /** How many are still missing, where a count is worth putting on screen. Zero otherwise. */
  faltan: number;
}

export interface Arranque {
  pasos: Paso[];
  hechos: number;
  total: number;
  /** True once there is something priced to sell; the till is opened from the home screen. */
  puedeVender: boolean;
  /** The first thing still undone, or null when the shop is ready. */
  siguiente: Paso | null;
  /** Worth doing soon, but nothing is blocked on them. */
  luego: ClaveLuego[];
  /** Dexie has not answered yet. Render nothing rather than a wrong answer. */
  cargando: boolean;
}

const VACIO: Arranque = {
  pasos: PASOS.map((clave) => ({ clave, hecho: false, faltan: 0 })),
  hechos: 0,
  total: PASOS.length,
  puedeVender: false,
  siguiente: null,
  luego: [],
  cargando: true,
};

/**
 * A product counts as priced when its first sale unit has a price on the default list.
 *
 * The first unit, not every unit: a product sold loose and by the sack whose sack has no price
 * is a pricing job, not a blocked shop, and the POS will catch the sack at the counter.
 */
function sinPrecio(
  productos: readonly { id: string; activo: boolean }[],
  unidades: readonly { id: string; productoId: string; orden?: number }[],
  precios: readonly { id: string; precio: number }[],
  listaId: string | null,
): number {
  if (!listaId) return productos.filter((p) => p.activo).length;
  return productos.filter((p) => {
    if (!p.activo) return false;
    const unidad = unidadesDe(unidades as never, p.id)[0];
    return !unidad || precioDe(precios as never, unidad.id, listaId) === null;
  }).length;
}

/**
 * The last answer, for the next screen that asks.
 *
 * Coming back to the home screen, the first frame is then the right screen rather than a blank
 * one — which the change of screen needs: a view transition takes its picture of the new screen
 * on that first frame, and a blank one has no «Vender» for the till to shrink back into. The very
 * first time there is no last answer, and it still waits rather than guess.
 */
let ultimo: Arranque | undefined;

export function useArranque(): Arranque {
  return (
    useLiveQuery(async () => {
      const [config, productos, unidades, precios, listas, proveedores] = await Promise.all([
        readSettings(),
        db.productos.toArray(),
        db.unidades.toArray(),
        db.precios.toArray(),
        db.listas.toArray(),
        db.proveedores.toArray(),
      ]);

      const activos = productos.filter((p) => p.activo);
      const lista = listaPredeterminada(listas.filter((l) => l.activa));
      const faltanPrecio = sinPrecio(productos, unidades, precios, lista?.id ?? null);

      const pasos: Paso[] = [
        // The default is the software's name, not the shop's. Until somebody changes it, the
        // header is telling the owner whose app this is rather than whose shop.
        {
          clave: 'nombre',
          hecho: config.negocioNombre !== DEFAULT_SETTINGS.negocioNombre,
          faltan: 0,
        },
        { clave: 'productos', hecho: activos.length > 0, faltan: 0 },
        {
          clave: 'precios',
          hecho: activos.length > 0 && faltanPrecio === 0,
          faltan: faltanPrecio,
        },
      ];

      const luego: ClaveLuego[] = [];
      if (proveedores.filter((p) => p.activo).length === 0) luego.push('proveedor');

      const hechos = pasos.filter((p) => p.hecho).length;
      return (ultimo = {
        pasos,
        hechos,
        total: pasos.length,
        // Naming the shop is a courtesy, not a prerequisite; the other two really do block.
        puedeVender: pasos.every((p) => p.clave === 'nombre' || p.hecho),
        siguiente: pasos.find((p) => !p.hecho) ?? null,
        luego,
        cargando: false,
      });
    }, [], ultimo) ?? VACIO
  );
}
